import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Recommend from '../components/Recommend';
import { AllCities } from '../services/region';

const HomePageWrapper = styled.div`
  width: 100%;
`;

const HomePageHeader = styled.h2`
  background-color: white;
  text-align: center;
`;

const HomePageContainer = styled.div`
  background-color: #F0F3F5;
  padding: 10px 20px 20px 20px;
`;

const LocationQ = styled.img`
  width: 200px;
  margin-top: 5px;
  cursor: pointer;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin-top: 5px;
`;

const ProductCard = styled.div`
  position: relative;
  width: 330px;
  height: 170px;
  background-color: white;
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 10px 0;
  border-radius: 12px;
  padding: 6px 0;
  cursor:pointer;

  img {
    width: 320px;
    height: 130px;
    border-radius: 12px;
    object-fit: cover;
  }

  .overlay {
    position: absolute;
    width: 280px;
    height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    color: white;
    border-radius: 12px;
    padding: 10px;
  }

  .overlay h1 {
    font-size: 28px;
    font-weight: bold;
    margin: 0;
  }

  .overlay .tags {
    display: flex;
    gap: 5px;

    div {
      font-size: 12px;
      background: rgba(255, 255, 255, 0.8);
      color: #000;
      border-radius: 12px;
      padding: 2px 8px;
    }
  }

  .bottom {
    width: 90%;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin: 6px 0;
  }

  .pre-bottom-price {
    font-size: 10px;
  }

  .bottom-price {
    font-size: 24px;
    font-weight: bolder;
  }
`;

const ContentMoreButton = styled.button`
  width: 350px;
  height: 40px;
  background-color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
`;

const HomePageBottom = styled.div`
  margin: 50px 25px;
  font-size: 12px;

  p > a {
    color: #000;
    
    &:hover {
      color: #000;
      text-decoration: underline;
    }

    &:visited {
      color: #000;
    }
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

function HomePage() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoading(true);
        const data = await AllCities();
        setCities(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCities();
  }, []);

  const visibleCities = cities.slice(0, currentPage * itemsPerPage);

  const handleShowMore = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const getCityImage = (cityName) => {
    const cityImages = {
      "New York": "./assets/country/america/NewYork.jpg",
      "Miami": "./assets/country/america/Miami.jpg",
      "Orlando": "./assets/country/america/Orlando.jpg",
      "San Francisco": "./assets/country/america/SanFrancisco.jpg",
      "Los Angeles": "./assets/country/america/LosAngeles.jpg",
      "Chicago": "./assets/country/america/Chicago.jpg"
    };
  
    return cityImages[cityName] || "./assets/country/tempBackground.jpg";
  };

  return (
    <>
      <Helmet>
        <title>MusePicker - Find & Compare Tickets in United States</title>
        <meta name="description" content="Compare ticket prices for attractions, tours, and activities in United States. Find the best deals on museum tickets, theme parks, shows and more." />
        <meta name="keywords" content="ticket comparison, activity tickets, museum tickets, tour tickets, attraction deals, booking.com, viator, tripadvisor, klook, trip.com, musepicker" />
        <meta name="author" content="Bannangco" />
        <meta property="og:title" content="MusePicker - Find & Compare Tickets in United States" />
        <meta property="og:description" content="Compare ticket prices for attractions in United States." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.musepicker.com" />
        <meta property="og:image" content="https://www.musepicker.com/assets/og-image.jpg" />
        <link rel="canonical" href="https://www.musepicker.com" />
      </Helmet>

      <HomePageWrapper>
        <HomePageHeader>FIND & COMPARE TICKETS</HomePageHeader>
        <HomePageContainer>
          <LocationQ src='./assets/WhereRUgoing.png' alt="location question"/>
          <ContentWrapper>
            {loading ? (<p>Loading...</p>) : error ? ( <p style={{ color: 'red' }}>Error: {error}</p>) :
              visibleCities.length === 0 ? (
                <p>No cities available.</p>
              ) : (
                visibleCities.map(city=> (
                  <StyledLink to={`/location/${city.city}`} key={city.id} style={{ textDecoration: 'none' }}>
                    <ProductCard>
                      <img src={getCityImage(city.city)} alt={city.city} />
                      <div className="overlay">
                        <h2>{city.city}, {city.state}</h2>
                        <div className="tags">
                          {city.categories.map((keyword, index) => (
                            <div key={index}>{keyword}</div>
                          ))}
                        </div>
                      </div>
                      <div className='bottom'>
                        <div>{city.totalCount}+ Things to Do</div>
                        <div><span className='pre-bottom-price'>from</span> <span className='bottom-price'>${city.lowestPrice}</span></div>
                      </div>
                    </ProductCard>
                  </StyledLink>
                ))
              )
            }
            {cities.length > itemsPerPage * currentPage && (
              <ContentMoreButton onClick={handleShowMore}> + Petition for More Cities</ContentMoreButton>
            )}
          </ContentWrapper>
        </HomePageContainer>
      </HomePageWrapper>
      <Recommend />
      <HomePageBottom>
        <strong>More</strong><br/>
        <p><a href="./">Airline fees</a></p>
        <p><a href="./">Airlines</a></p>
        <p><a href="./">Low fare tips</a></p>
        <p><a href="./">Badges & Certificates</a></p>
        <p><a href="./">Security</a></p>
      </HomePageBottom>
    </>
  );
}

export default HomePage;